import type { Client, ConditionGroup, CreateClientPayload, NextPageParams, PaginationMetadata } from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"
import { BulkAddHouseholdsToProjectResponse } from "@/hooks/api/projects/types"

export interface ClientsResponse {
  clients: Client[]
  pagination: PaginationMetadata
  total_aum: number
}

export function fetchClients(
  context: ApiContext,
  viewFilterGroup?: ConditionGroup,
  nextPageParams?: NextPageParams,
  searchTerm?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" },
  listId?: string
) {
  const params = new URLSearchParams()

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  appendViewSortParams(params, viewSort)

  if (listId) {
    params.append("list_id", listId)
  }

  return apiContextFetch<ClientsResponse>(context, `clients?${params.toString()}`, {
    method: "GET",
  })
}

export function fetchClient(context: ApiContext, id: string) {
  return apiContextFetch<Client>(context, `clients/${id}`)
}

export function createClient(context: ApiContext, params: CreateClientPayload, overrideBookId?: string) {
  return apiContextFetch<Client>(context, "clients", {
    method: "POST",
    body: JSON.stringify({ client: params, override_book_id: overrideBookId }),
  })
}

export interface UpdateClientPayload {
  custom_fields_attributes?: Record<string, unknown>
  [key: string]: unknown
}

export function updateClient(context: ApiContext, id: string, params: UpdateClientPayload) {
  return apiContextFetch<Client>(context, `clients/${id}`, {
    method: "PUT",
    body: JSON.stringify({ client: params }),
  })
}

export interface BulkUpdateClientsPayload {
  next_review_due_on_override?: string
  client_ids: string[]
  tier?: number | null
  tag_id?: string
  remove_tag_id?: string
  custom_field_key?: string
  custom_field_value?: string | string[] | number | boolean | null
}

export function bulkUpdateClients(context: ApiContext, params: BulkUpdateClientsPayload) {
  return apiContextFetch<{ updated_count: number }>(context, "clients/bulk_update", {
    method: "PATCH",
    body: JSON.stringify(params),
  })
}

export interface BulkAddClientsToProjectPayload {
  client_ids: string[]
  project_template_id: string
  assigned_to_id: string | null
  assigned_team_id: string | null
}

export function bulkAddClientsToProject(context: ApiContext, params: BulkAddClientsToProjectPayload) {
  return apiContextFetch<BulkAddHouseholdsToProjectResponse>(context, "clients/bulk_add_to_project", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface BulkAddClientsToSequencePayload {
  client_ids: string[]
  sequence_id: string
  email_integration_id?: string | null
  sms_integration?: string | null
}

export interface BulkAddClientsToSequenceResponse {
  queued_count: number
  message: string
}

export function bulkAddClientsToSequence(context: ApiContext, params: BulkAddClientsToSequencePayload) {
  return apiContextFetch<BulkAddClientsToSequenceResponse>(context, "clients/bulk_add_to_sequence", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface AllClientIdsResponse {
  client_ids: string[]
  total_count: number
}

export function fetchAllClientIds(context: ApiContext, viewFilterGroup?: ConditionGroup, searchTerm?: string) {
  const params = new URLSearchParams()

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  return apiContextFetch<AllClientIdsResponse>(context, `clients/all_ids?${params.toString()}`, {
    method: "GET",
  })
}

export interface BulkCreateClientsPayload {
  file_name?: string
  override_duplicates?: boolean
  records: Array<{
    household_id?: string
    contact_type?: string
    full_name?: string
    first_name?: string
    last_name?: string
    email?: string
    phone_number?: string
    status?: string
    tier?: string
    address_line1?: string
    address_line2?: string
    address_city?: string
    address_state?: string
    address_zip?: string
    address_country_code?: string
    address_type?: string
    is_primary_address?: boolean
    address_2_line1?: string
    address_2_line2?: string
    address_2_city?: string
    address_2_state?: string
    address_2_zip?: string
    address_2_country_code?: string
    address_2_type?: string
    // Protected/additional person fields
    ssn?: string
    date_of_birth?: string
    gender?: string
    marital_status?: string
    job_title?: string
    salutation?: string
    suffix?: string
    middle_name?: string
    preferred_name?: string
    previous_name?: string
    designations?: string
    // Custom fields
    custom_fields?: Record<string, unknown>
    person_custom_fields?: Record<string, unknown>
  }>
}

export interface BulkCreateResponse {
  list_id: string
}

export function bulkCreateClients(context: ApiContext, params: BulkCreateClientsPayload) {
  return apiContextFetch<BulkCreateResponse>(context, "clients/bulk_create", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export function bulkDestroyClients(context: ApiContext, clientIds: string[]) {
  return apiContextFetch<Client>(context, `clients/bulk_destroy`, {
    method: "DELETE",
    body: JSON.stringify({ client_ids: clientIds }),
  })
}

export async function demoteToProspect(context: ApiContext, clientId: string) {
  return await apiContextFetch<Client>(context, `clients/${clientId}/demote_to_prospect`, {
    method: "POST",
  })
}

export interface BulkDemoteProspectsPayload {
  client_ids: string[]
}

export function bulkDemoteToProspect(context: ApiContext, params: BulkDemoteProspectsPayload) {
  return apiContextFetch<{ demoted_count: number; failed_count: number }>(context, "clients/bulk_demote_to_prospect", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export async function archiveToPastClient(context: ApiContext, clientId: string) {
  return await apiContextFetch<Client>(context, `clients/${clientId}/archive_to_past_client`, {
    method: "POST",
  })
}

export interface BulkArchiveToPastClientPayload {
  client_ids: string[]
}

export function bulkArchiveToPastClient(context: ApiContext, params: BulkArchiveToPastClientPayload) {
  return apiContextFetch<{ archived_count: number; failed_count: number }>(
    context,
    "clients/bulk_archive_to_past_client",
    {
      method: "POST",
      body: JSON.stringify(params),
    }
  )
}

export interface PortfolioCompositionResponse {
  composition: Record<
    string,
    {
      absolute_value: number | null
      percentage: number | null
      asset_class?: string | null
      type?: string | null
      security_type?: string | null
      description?: string | null
      equity_percentage?: number | null
      fixed_income_percentage?: number | null
      dominant_class?: string | null
    }
  >
  asset_class_composition: Record<
    string,
    {
      absolute_value: number | null
      percentage: number | null
    }
  >
  total_value: number | null
  pricing_status?: "complete" | "stale" | "incomplete" | null
  positions_as_of?: string | null
}

export type PortfolioOwnerType = "clients" | "businesses"

export function fetchPortfolioComposition(
  context: ApiContext,
  ownerId: string,
  accountIds?: string[],
  ownerType: PortfolioOwnerType = "clients"
) {
  const searchParams = new URLSearchParams()
  accountIds?.forEach((id) => searchParams.append("account_ids[]", id))

  const path = searchParams.toString()
    ? `${ownerType}/${ownerId}/portfolio_composition?${searchParams.toString()}`
    : `${ownerType}/${ownerId}/portfolio_composition`

  return apiContextFetch<PortfolioCompositionResponse>(context, path)
}

export interface PortfolioBalanceTodayResponse {
  latest_total_balance: number | null
  balance_change: number | null
  percentage_change: number | null
  as_of_dates: Record<string, number>
}

export function fetchPortfolioBalanceToday(
  context: ApiContext,
  ownerId: string,
  accountIds?: string[],
  ownerType: PortfolioOwnerType = "clients"
) {
  const searchParams = new URLSearchParams()
  accountIds?.forEach((id) => searchParams.append("account_ids[]", id))

  const path = searchParams.toString()
    ? `${ownerType}/${ownerId}/portfolio_balance_today?${searchParams.toString()}`
    : `${ownerType}/${ownerId}/portfolio_balance_today`

  return apiContextFetch<PortfolioBalanceTodayResponse>(context, path)
}

export interface PortfolioTransaction {
  id: string
  account_id: string
  account_name: string | null
  received_from_account_number: string | null
  security_symbol: string | null
  transaction_date: string
  transaction_type: string | null
  transaction_subtype: string | null
  description: string | null
  quantity: number | null
  unit_price: number | null
  gross_amount: number | null
  net_amount: number | null
  fees: number | null
  currency: string | null
  custodian: string | null
}

export interface PortfolioTransactionsResponse {
  transactions: PortfolioTransaction[]
}

export function fetchPortfolioTransactions(
  context: ApiContext,
  ownerId: string,
  params?: { limit?: number; lookback_days?: number; account_ids?: string[] },
  ownerType: PortfolioOwnerType = "clients"
) {
  const searchParams = new URLSearchParams()
  if (params?.limit) {
    searchParams.append("limit", params.limit.toString())
  }
  if (params?.lookback_days) {
    searchParams.append("lookback_days", params.lookback_days.toString())
  }
  params?.account_ids?.forEach((id) => searchParams.append("account_ids[]", id))

  const query = searchParams.toString()

  const path = query
    ? `${ownerType}/${ownerId}/portfolio_transactions?${query}`
    : `${ownerType}/${ownerId}/portfolio_transactions`

  return apiContextFetch<PortfolioTransactionsResponse>(context, path)
}

export interface ClientCustodianAccount {
  feed_unavailable_at: string | null
  as_of_date: string | null
  id: string
  lake_account_id: string
  name: string
  custodian: string | null
  account_type_normalized: string | null
  masked_account_number: string | null
  total_market_value: number | null
  closed_on: string | null
}

export interface ClientReportingAccount {
  id: string
  name: string
  custodian: string | null
  category: string | null
  market_value: number | null
  performance_return: number | null
  starting_value: number | null
  contributions: number | null
  withdrawals: number | null
  fees: number | null
  income: number | null
  as_of_date: string | null
  masked_account_number: string | null
  owner_names: string[]
  external_url: string
  projected_income: number | null
  extra_attributes: Record<string, string>
}

export interface ReportingExtraAttributeLabel {
  key: string
  display_name: string
}

export interface ReportingAllocationPathSegment {
  dimension: string
  remote_id?: string
  label: string
}

export interface ReportingAllocationNode {
  path: ReportingAllocationPathSegment[]
  amount?: string
  supervised_amount?: string
  unsupervised_amount?: string
  allocation?: string
}

export interface ReportingAllocationTree {
  groupings: string[]
  nodes: ReportingAllocationNode[]
}

export interface ReportingAllocationTarget extends ReportingAllocationTree {
  remote_id?: string
  name?: string
  description?: string
  start_date?: string
  end_date?: string
  provider_metadata?: Record<string, string | number | boolean | null>
}

export interface ClientReportingAllocationScope {
  id: string
  name: string
  remote_type: string
  account_ids: string[]
  primary: boolean
  current_allocations: ReportingAllocationTree[]
  allocation_targets: ReportingAllocationTarget[]
  as_of_date: string | null
  performance_return: number | null
}

export interface ClientReportingAccountsResponse {
  accounts: ClientReportingAccount[]
  allocation_scopes: ClientReportingAllocationScope[]
  performance_return?: number | null
  extra_attributes: Record<string, string>
  extra_attribute_labels: ReportingExtraAttributeLabel[]
}

export type ClientReportingPeriod = "ytd" | "prior_year" | "since_inception"

export function fetchClientReportingAccounts(context: ApiContext, clientId: string, period: ClientReportingPeriod) {
  const params = new URLSearchParams({ period })
  return apiContextFetch<ClientReportingAccountsResponse>(
    context,
    `clients/${clientId}/reporting_accounts?${params.toString()}`
  )
}

export function fetchPortfolioCustodianAccounts(
  context: ApiContext,
  ownerId: string,
  ownerType: PortfolioOwnerType = "clients"
) {
  return apiContextFetch<ClientCustodianAccount[]>(context, `${ownerType}/${ownerId}/custodian_accounts`)
}

export interface PortfolioRealGrowthDataPoint {
  as_of_date: string
  total_value: number
}

export interface PortfolioRealGrowthResponse {
  data_points: PortfolioRealGrowthDataPoint[]
}

export function fetchPortfolioRealGrowth(
  context: ApiContext,
  ownerId: string,
  accountIds?: string[],
  ownerType: PortfolioOwnerType = "clients"
) {
  const searchParams = new URLSearchParams()
  accountIds?.forEach((id) => searchParams.append("account_ids[]", id))

  const path = searchParams.toString()
    ? `${ownerType}/${ownerId}/portfolio_real_growth?${searchParams.toString()}`
    : `${ownerType}/${ownerId}/portfolio_real_growth`

  return apiContextFetch<PortfolioRealGrowthResponse>(context, path)
}

export interface BulkRenameClientsPayload {
  client_ids: string[]
  templates: {
    include_spouse: boolean
    no_spouse_template: string
    same_last_name_template: string
    different_last_name_template: string
  }
}

export function bulkRenameClients(context: ApiContext, params: BulkRenameClientsPayload) {
  return apiContextFetch<{ renamed_ids: string[] }>(context, "clients/bulk_rename", {
    method: "PATCH",
    body: JSON.stringify(params),
  })
}

export interface ClientAddeparReport {
  id: string
  name: string
  pie_attribute_keys: string[]
  result: { id: string; as_of_date: string } | null
}

export interface ClientAddeparReportsResponse {
  addepar_reports: ClientAddeparReport[]
}

export interface AddeparReportColumn {
  key: string
  display_name: string
  output_type: string
  currency?: string | null
}

export interface AddeparReportGrouping {
  key: string
  display_name: string
}

export interface AddeparReportCellObject {
  display_name?: string | null
  name?: string | null
  value?: string | number | null
}

export type AddeparReportCell = number | string | boolean | null | AddeparReportCellObject | AddeparReportCell[]

export interface AddeparReportNode {
  name: string | null
  grouping: string | null
  entity_id?: string | number | null
  columns: Record<string, AddeparReportCell | undefined>
  children?: AddeparReportNode[]
}

export interface AddeparReportPieSlice {
  label: string
  value: number
}

export interface AddeparReportPie {
  key: string
  display_name: string
  currency: string | null
  slices: AddeparReportPieSlice[]
}

export interface AddeparReportResult {
  id: string
  as_of_date: string
  report: { id: string; name: string; pie_attribute_keys: string[] }
  columns: AddeparReportColumn[]
  groupings: AddeparReportGrouping[]
  rows: AddeparReportNode
  pies: AddeparReportPie[]
}

export function fetchClientAddeparReports(context: ApiContext, clientId: string, reportingHouseholdId?: string) {
  const params = new URLSearchParams()
  if (reportingHouseholdId) params.set("reporting_household_id", reportingHouseholdId)
  return apiContextFetch<ClientAddeparReportsResponse>(context, `clients/${clientId}/addepar_reports?${params}`)
}

export function fetchAddeparReportResult(context: ApiContext, resultId: string) {
  return apiContextFetch<AddeparReportResult>(context, `addepar_report_results/${resultId}`)
}
